import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate,useLocation } from 'react-router-dom';
import { timesheetsApi, Timesheet } from '../../api/timesheetsApi';
import { projectsApi, Project } from '../../api/projectsApi';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Loader } from '../../components/common/Loader';
import { Icons } from '../../components/common/Icons';
import { SortIcon } from '../../components/common/SortIcon';
import { useSorting, applySorting } from '../../hooks/useSorting';
import { formatDate, downloadBlob } from '../../utils/helpers';
import { STATUS_COLORS } from '../../utils/constants';
import { MyTimesheetStats } from './MyTimesheetStats';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../types';
import './Timesheets.css';
import './MyTimesheetStats.css';
// import { useLocation } from 'react-router-dom';

type StatusFilter = 'all' | 'submitted' | 'approved' | 'rejected';

// Column configuration
interface ColumnConfig {
  key: string;
  label: string;
  defaultVisible: boolean;
}

const ALL_COLUMNS: ColumnConfig[] = [
  { key: 'date', label: 'Date', defaultVisible: true },
  { key: 'day', label: 'Day', defaultVisible: false },
  { key: 'project', label: 'Project', defaultVisible: true },
  { key: 'task', label: 'Tasks', defaultVisible: true },
  { key: 'description', label: 'Description', defaultVisible: true },
  { key: 'hours', label: 'Hours Spent', defaultVisible: true },
  { key: 'reject_reason', label: 'Reject Reason', defaultVisible: false },
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'created_at', label: 'Created At', defaultVisible: false },
  { key: 'updated_at', label: 'Updated At', defaultVisible: false },
];

const DEFAULT_VISIBLE_COLUMNS = ALL_COLUMNS.filter(c => c.defaultVisible).map(c => c.key);

type TimesheetSortKey = 'date' | 'day' | 'project' | 'task' | 'description' | 'hours' | 'status' | 'created_at' | 'updated_at';

// Map frontend column keys to backend export column keys
const EXPORT_COLUMN_MAP: Record<string, string> = {
  project: 'project_name',
  task: 'task_type',
};

export function Timesheets() {
  const navigate = useNavigate();
  const { userRole } = useAuth();
  const isEmployee = userRole === UserRole.EMPLOYEE;

  const location = useLocation();

  // State
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Filters
  // const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(
  location.state?.status || 'all'
);
  const [projectFilter, setProjectFilter] = useState<number | ''>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState<{ submitted: number; approved: number; rejected: number }>({ submitted: 0, approved: 0, rejected: 0 });
  const [allCount, setAllCount] = useState(0);


  useEffect(() => {
  if (location.state?.status) {
    setStatusFilter(location.state.status);
  }
}, [location.state]);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<string[]>(DEFAULT_VISIBLE_COLUMNS);
  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [showInlineColumnMenu, setShowInlineColumnMenu] = useState(false);
  const columnMenuRef = useRef<HTMLDivElement>(null);
  const inlineColumnMenuRef = useRef<HTMLTableCellElement>(null);
  
  // Export state
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target as Node)) {
        setShowColumnMenu(false);
      }
      if (inlineColumnMenuRef.current && !inlineColumnMenuRef.current.contains(event.target as Node)) {
        setShowInlineColumnMenu(false);
      }
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setShowExportMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);


  // Sorting state
  const { sortConfig, handleSort, getSortDirection, isColumnSorted } = useSorting<TimesheetSortKey>('date', 'desc');

  // Actions
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const [editingTimesheet, setEditingTimesheet] = useState<Timesheet | null>(null);

  const fetchTimesheets = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await timesheetsApi.getMyTimesheets({
        status: statusFilter === 'all' ? undefined : statusFilter,
        project_id: projectFilter || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        page,
        per_page: 20,
      });
      
      setTimesheets(response.timesheets);
      setTotalPages(response.pages || 1);
      setTotal(response.total);
      if (response.status_counts) {
        setStatusCounts(response.status_counts);
      }
      if (response.all_count !== undefined) {
        setAllCount(response.all_count);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load timesheets');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, projectFilter, startDate, endDate, page]);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await projectsApi.getMyProjects();
      setProjects(response.projects);
    } catch (err) {
      console.error('Failed to load projects:', err);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    fetchTimesheets();
  }, [fetchTimesheets]);

  const handleDelete = async (timesheetId: number) => {
    if (!window.confirm('Are you sure you want to delete this timesheet entry?')) {
      return;
    }
    
    try {
      setActionLoading(timesheetId);
      await timesheetsApi.deleteTimesheet(timesheetId);
      setSuccessMessage('Timesheet deleted successfully!');
      fetchTimesheets();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete timesheet');
    } finally {
      setActionLoading(null);
    }
  };

  const handleClearFilters = () => {
    setStatusFilter('all');
    setProjectFilter('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const handleColumnToggle = (columnKey: string) => {
    const newColumns = visibleColumns.includes(columnKey)
      ? visibleColumns.filter(c => c !== columnKey)
      : [...visibleColumns, columnKey];
    // Keep at least one column visible
    if (newColumns.length > 0) {
      setVisibleColumns(newColumns);
    }
  };

  const handleExport = async (format: 'csv' | 'xlsx') => {
    try {
      setExportLoading(true);
      setShowExportMenu(false);
      // Map frontend column keys to backend export column keys
      const exportColumns = visibleColumns.map(col => EXPORT_COLUMN_MAP[col] || col);
      const blob = await timesheetsApi.exportMyTimesheets({
        format,
        status: statusFilter === 'all' ? undefined : statusFilter,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        project_id: projectFilter || undefined,
        columns: exportColumns.join(','),
      });
      downloadBlob(blob, `my_timesheets.${format}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export timesheets');
    } finally {
      setExportLoading(false);
    }
  };

  // Helper to get day abbreviation from date string
  const getDayName = (dateStr: string) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { weekday: 'short' });
  };

  // Sorted timesheets
  const sortedTimesheets = applySorting(timesheets, sortConfig, (timesheet, key) => {
    switch (key) {
      case 'date': return new Date(timesheet.date).getTime();
      case 'day': return getDayName(timesheet.date);
      case 'project': return timesheet.project_name || '';
      case 'task': return timesheet.task_type_name || '';
      case 'description': return timesheet.description || '';
      case 'hours': return timesheet.hours;
      case 'status': return timesheet.status;
      case 'created_at': return timesheet.created_at ? new Date(timesheet.created_at).getTime() : 0;
      case 'updated_at': return timesheet.updated_at ? new Date(timesheet.updated_at).getTime() : 0;
      default: return '';
    }
  });

  // Helper to format datetime
  const formatDateTime = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="timesheets-page">
      <header className="page-header">
        <div className="header-content">
          <div className="header-title">
            <h1>My Timesheets</h1>
          </div>
          <div className="header-actions-group">
            {/* Column selector */}
            <div className="dropdown-container" ref={columnMenuRef}>
              <Button
                variant="ghost"
                onClick={() => setShowColumnMenu(!showColumnMenu)}
              >
                <Icons.Columns />
                Columns
              </Button>
              {showColumnMenu && (
                <div className="dropdown-menu columns-menu">
                  <div className="dropdown-header">Toggle Columns</div>
                  {ALL_COLUMNS.map(col => (
                    <label key={col.key} className="dropdown-item checkbox-item">
                      <input
                        type="checkbox"
                        checked={visibleColumns.includes(col.key)}
                        onChange={() => handleColumnToggle(col.key)}
                      />
                      <span>{col.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Export button */}
            <div className="dropdown-container" ref={exportMenuRef}>
              <Button
                variant="secondary"
                onClick={() => setShowExportMenu(!showExportMenu)}
                disabled={exportLoading || timesheets.length === 0}
              >
                <Icons.Download />
                Export
              </Button>
              {showExportMenu && (
                <div className="dropdown-menu">
                  <button className="dropdown-item" onClick={() => handleExport('csv')}>
                    <Icons.FileText />
                    Export as CSV
                  </button>
                  <button className="dropdown-item" onClick={() => handleExport('xlsx')}>
                    <Icons.FileSpreadsheet />
                    Export as Excel
                  </button>
                </div>
              )}
            </div>

            <Button
              variant="primary"
              onClick={() => navigate('/log-time')}
            >
              <Icons.Clock />
              Fill Timesheet
            </Button>
          </div>
        </div>
      </header>

      {error && (
        <div className="alert alert-error">
          <Icons.AlertCircle />
          <span>{error}</span>
          <button className="alert-close" onClick={() => setError(null)}>
            <Icons.X />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <Icons.CheckCircle />
          <span>{successMessage}</span>
        </div>
      )}

      {/* My Timesheet Stats - Overview Section (hidden for Employee role - same as dashboard) */}
      {!isEmployee && <MyTimesheetStats />}

      {/* Status Tabs */}
      <div className="status-tabs">
        <button
          className={`status-tab ${statusFilter === 'all' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('all'); setPage(1); }}
        >
          All
          <span className="tab-count">{allCount}</span>
        </button>
        <button
          className={`status-tab ${statusFilter === 'submitted' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('submitted'); setPage(1); }}
        >
          Pending
          <span className="tab-count submitted">{statusCounts.submitted}</span>
        </button>
        <button
          className={`status-tab ${statusFilter === 'approved' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('approved'); setPage(1); }}
        >
          Approved
          <span className="tab-count approved">{statusCounts.approved}</span>
        </button>
        <button
          className={`status-tab ${statusFilter === 'rejected' ? 'active' : ''}`}
          onClick={() => { setStatusFilter('rejected'); setPage(1); }}
        >
          Rejected
          <span className="tab-count rejected">{statusCounts.rejected}</span>
        </button>
      </div>

      {/* Filters */}
      <Card className="filters-card">
        <div className="filters-row">
          <div className="filter-group">
            <label>Project</label>
            <div className="filter-select-wrapper">
              <select
                value={projectFilter}
                onChange={(e) => { setProjectFilter(e.target.value ? Number(e.target.value) : ''); setPage(1); }}
                className="filter-select"
              >
                <option value="">All Projects</option>
                {projects.map((project) => (
                  <option key={project.id} value={project.id}>
                    {project.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="filter-group">
            <label>From Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
              max={new Date().toISOString().split('T')[0]}
              className="filter-input"
            />
          </div>
          
          <div className="filter-group">
            <label>To Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
              max={new Date().toISOString().split('T')[0]}
              className="filter-input"
            />
          </div>
          
          <button
            className="clear-filters-btn"
            onClick={handleClearFilters}
          >
            <Icons.X />
            Clear
          </button>
        </div>
      </Card>

      {/* Timesheets List */}
      <Card className="timesheets-list-card">
        {loading ? (
          <div className="loading-container">
            <Loader text="Loading..." />
          </div>
        ) : timesheets.length === 0 ? (
          <div className="empty-state">
            <Icons.FileText />
            <h3>No timesheets found</h3>
            <p>
              {statusFilter !== 'all' 
                ? `You don't have any ${statusFilter} timesheets.` 
                : 'You haven\'t logged any time yet.'}
            </p>
            <Button variant="primary" onClick={() => navigate('/log-time')}>
              Log Your First Entry
            </Button>
          </div>
        ) : (
          <>
            <div className="timesheets-table-container">
              <table className="timesheets-table">
                <thead>
                  <tr>
                    {visibleColumns.includes('date') && (
                      <th
                        className="th-date th-sortable"
                        onClick={() => handleSort('date')}
                      >
                        Date
                        <SortIcon direction={getSortDirection('date')} isActive={isColumnSorted('date')} />
                      </th>
                    )}
                    {visibleColumns.includes('day') && (
                      <th className="th-day th-sortable" onClick={() => handleSort('day')}>
                        Day
                        <SortIcon direction={getSortDirection('day')} isActive={isColumnSorted('day')} />
                      </th>
                    )}
                    {visibleColumns.includes('project') && (
                      <th className="th-project th-sortable" onClick={() => handleSort('project')}>
                        Project
                        <SortIcon direction={getSortDirection('project')} isActive={isColumnSorted('project')} />
                      </th>
                    )}
                    {visibleColumns.includes('task') && (
                      <th className="th-task th-sortable" onClick={() => handleSort('task')}>
                        Tasks
                        <SortIcon direction={getSortDirection('task')} isActive={isColumnSorted('task')} />
                      </th>
                    )}
                    {visibleColumns.includes('description') && (
                      <th className="th-description th-sortable" onClick={() => handleSort('description')}>
                        Description
                        <SortIcon direction={getSortDirection('description')} isActive={isColumnSorted('description')} />
                      </th>
                    )}
                    {visibleColumns.includes('hours') && (
                      <th className="th-hours th-sortable" onClick={() => handleSort('hours')}>
                        Hours Spent
                        <SortIcon direction={getSortDirection('hours')} isActive={isColumnSorted('hours')} />
                      </th>
                    )}
                    {visibleColumns.includes('reject_reason') && <th className="th-reject">Reject Reason</th>}
                    {visibleColumns.includes('status') && (
                      <th className="th-status th-sortable" onClick={() => handleSort('status')}>
                        Status
                        <SortIcon direction={getSortDirection('status')} isActive={isColumnSorted('status')} />
                      </th>
                    )}
                    {visibleColumns.includes('created_at') && (
                      <th className="th-created th-sortable" onClick={() => handleSort('created_at')}>
                        Created At
                        <SortIcon direction={getSortDirection('created_at')} isActive={isColumnSorted('created_at')} />
                      </th>
                    )}
                    {visibleColumns.includes('updated_at') && (
                      <th className="th-updated th-sortable" onClick={() => handleSort('updated_at')}>
                        Updated At
                        <SortIcon direction={getSortDirection('updated_at')} isActive={isColumnSorted('updated_at')} />
                      </th>
                    )}
                    <th className="th-actions th-column-toggle" ref={inlineColumnMenuRef}>
                      <button
                        type="button"
                        className="th-column-toggle-btn"
                        onClick={() => setShowInlineColumnMenu(!showInlineColumnMenu)}
                        title="Toggle columns"
                      >
                        <Icons.Columns />
                      </button>
                      {showInlineColumnMenu && (
                        <div className="dropdown-menu columns-menu inline-columns-menu">
                          <div className="dropdown-header">Toggle Columns</div>
                          {ALL_COLUMNS.map(col => (
                            <label key={col.key} className="dropdown-item checkbox-item">
                              <input
                                type="checkbox"
                                checked={visibleColumns.includes(col.key)}
                                onChange={() => handleColumnToggle(col.key)}
                              />
                              <span>{col.label}</span>
                            </label>
                          ))}
                        </div>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTimesheets.map((timesheet) => (
                    <tr key={timesheet.id}>
                      {visibleColumns.includes('date') && (
                        <td className="date-cell">
                          <span className="date-value">{formatDate(timesheet.date)}</span>
                        </td>
                      )}
                      {visibleColumns.includes('day') && (
                        <td className="day-cell">{getDayName(timesheet.date)}</td>
                      )}
                      {visibleColumns.includes('project') && (
                        <td>
                          <span className="project-name">{timesheet.project_name}</span>
                        </td>
                      )}
                      {visibleColumns.includes('task') && (
                        <td>{timesheet.task_type_name}</td>
                      )}
                      {visibleColumns.includes('description') && (
                        <td className="description-cell">
                          {timesheet.description || '-'}
                        </td>
                      )}
                      {visibleColumns.includes('hours') && (
                        <td className="hours-cell">
                          {timesheet.hours.toFixed(2)}
                        </td>
                      )}
                      {visibleColumns.includes('reject_reason') && (
                        <td className="reject-reason-cell">
                          {timesheet.reject_reason || '-'}
                        </td>
                      )}
                      {visibleColumns.includes('status') && (
                        <td className="td-status">
                          <span
                            className="status-badge"
                            style={{
                              backgroundColor: `${STATUS_COLORS[timesheet.status]}15`,
                              color: STATUS_COLORS[timesheet.status],
                            }}
                          >
                            {timesheet.status}
                          </span>
                        </td>
                      )}
                      {visibleColumns.includes('created_at') && (
                        <td className="datetime-cell">
                          {formatDateTime(timesheet.created_at)}
                        </td>
                      )}
                      {visibleColumns.includes('updated_at') && (
                        <td className="datetime-cell">
                          {formatDateTime(timesheet.updated_at)}
                        </td>
                      )}
                      <td className="td-actions">
                        <div className="action-buttons">
                          {timesheet.status === 'submitted' && (
                            <>
                              <button
                                className="action-btn edit"
                                onClick={() => navigate(`/log-time?edit=${timesheet.id}`)}
                                title="Edit entry"
                              >
                                <Icons.Edit />
                              </button>
                              <button
                                className="action-btn delete"
                                onClick={() => handleDelete(timesheet.id)}
                                disabled={actionLoading === timesheet.id}
                                title="Delete"
                              >
                                <Icons.Trash />
                              </button>
                            </>
                          )}
                          {(timesheet.status === 'approved' || timesheet.status === 'rejected') && (
                            <span className="locked-badge">
                              <Icons.Lock />
                              Locked
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  className="pagination-btn"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <Icons.ChevronLeft />
                  Previous
                </button>
                <span className="pagination-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  className="pagination-btn"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                  <Icons.ChevronRight />
                </button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

export default Timesheets;
